import { type ReactNode, useEffect, useState } from "react";
import useSWR, { mutate } from "swr";
import { useParams } from "react-router-dom";
import { ExternalLink, Play, RefreshCw, Save, Trash2, Video } from "lucide-react";
import { toast } from "sonner";
import { AMENITY_KEYS } from "@citybrews/place-core/amenities";
import {
  CashPolicySchema,
  CrowdTagSchema,
  GoodForTagSchema,
  MichelinStatusSchema,
  NoiseLevelSchema,
  PlaceFormatSchema,
  PriceTierSchema,
  QualityFlagSchema,
  ReservationsPolicySchema,
  TouristRatioSchema,
  VibeTagSchema,
} from "@citybrews/shared/schemas";
import type { AdminPlaceClipsPatchRequest, AdminPlaceGraph, AdminPlacePatchRequest, AdminPlacePhotosPatchRequest, AdminPlaceScorePatchRequest } from "@citybrews/shared/adminSchemas";
import { apiGet, apiSend } from "../api";
import type { PlaceForm } from "../types";
import { EmptyState, Field, NativeSelect, numberOrNull, PageError, SwitchField, TagEditor, textOrNull } from "../ui-helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetFooter, SheetHeader } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

const fetcher = <T,>(path: string) => apiGet<T>(path);

const options = {
  format: PlaceFormatSchema.options,
  priceTier: PriceTierSchema.options,
  noiseLevel: NoiseLevelSchema.options,
  vibeTags: VibeTagSchema.options,
  crowdTags: CrowdTagSchema.options,
  goodFor: GoodForTagSchema.options,
  michelinStatus: MichelinStatusSchema.options,
  reservations: ReservationsPolicySchema.options,
  touristRatio: TouristRatioSchema.options,
  cashPolicy: CashPolicySchema.options,
  qualityFlags: QualityFlagSchema.options,
  amenities: AMENITY_KEYS,
  hygieneVerdict: ["pass", "flag", "cut"] as const,
  businessStatus: ["OPERATIONAL", "CLOSED_TEMPORARILY", "CLOSED_PERMANENTLY"] as const,
};

// Place-scoped rerun stages proxied to the pipeline orchestrator via
// POST /admin/api/places/:id/rerun. "extract" is deliberately absent — it is
// the city-wide discovery sweep and cannot be scoped to a single place.
const rerunStageOptions = [
  { value: "media", label: "Photos & videos" },
  { value: "reviews", label: "Collect reviews" },
  { value: "classify", label: "Fill in details" },
  { value: "hygiene", label: "Quality check" },
  { value: "assemble", label: "Publish" },
  { value: "republish", label: "Republish" },
] as const;
type RerunStage = (typeof rerunStageOptions)[number]["value"];

const scoreFields = {
  environment: "Environment",
  wifi: "WiFi",
  outlets: "Outlets",
  price: "Price",
  noise: "Noise",
} as const;
const scoreKeys = Object.keys(scoreFields) as Array<keyof typeof scoreFields>;
const placeEditorTabs = {
  overview: "Overview",
  media: "Photos & videos",
  identity: "Identity",
  classification: "Details",
  hygiene: "Quality check",
  scores: "Scores",
  pipeline: "Pipeline",
} as const;
type PlaceEditorTab = keyof typeof placeEditorTabs;
const placeEditorTabOrder = Object.keys(placeEditorTabs) as PlaceEditorTab[];

export function PlaceEditorPage() {
  const { slug, placeId } = useParams();
  if (!slug || !placeId) return null;
  return <PlaceEditorContent slug={slug} placeId={placeId} />;
}

export function PlaceEditorSheet({ slug, placeId, onOpenChange }: { slug: string; placeId: string | null; onOpenChange: (open: boolean) => void }) {
  return (
    <Sheet open={!!placeId} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full !max-w-none gap-0 overflow-hidden p-0 data-[side=right]:!w-full data-[side=right]:!max-w-none md:!min-w-[480px] md:!w-[50vw] md:!max-w-[760px] md:data-[side=right]:!min-w-[480px] md:data-[side=right]:!w-[50vw] md:data-[side=right]:!max-w-[760px]">
        {placeId ? <PlaceEditorContent slug={slug} placeId={placeId} sheet /> : null}
      </SheetContent>
    </Sheet>
  );
}

function PlaceEditorContent({ slug, placeId, sheet = false }: { slug: string; placeId: string; sheet?: boolean }) {
  const path = `/admin/api/places/${encodeURIComponent(placeId)}`;
  const { data, error, isLoading } = useSWR<AdminPlaceGraph>(path, fetcher);
  const [form, setForm] = useState<PlaceForm | null>(null);
  const [activeTab, setActiveTab] = useState<PlaceEditorTab>("overview");
  const [rerunStage, setRerunStage] = useState<RerunStage>("media");
  useEffect(() => { if (data) setForm(placeFormFromGraph(data)); }, [data]);

  if (error) {
    if (sheet) {
      return (
        <PlaceEditorSheetFrame header={<PlaceEditorSheetLoadingHeader />} footer={null}>
          <PageError error={error} />
        </PlaceEditorSheetFrame>
      );
    }
    return <PageError error={error} />;
  }

  if (isLoading || !data || !form) {
    const loadingState = <div className={sheet ? "p-4" : "p-6"}><Skeleton className="h-[70vh]" /></div>;
    if (sheet) {
      return (
        <PlaceEditorSheetFrame header={<PlaceEditorSheetLoadingHeader />} footer={null}>
          {loadingState}
        </PlaceEditorSheetFrame>
      );
    }
    return loadingState;
  }

  const current = form;
  const media = mediaFromGraph(data);
  const status = statusForPlace(data);
  const appScores = appScoresFromGraph(data);
  const scoreRows = scoreKeys.map((key) => ({ key, label: scoreFields[key], value: appScores?.[key] ?? null }));
  const compositeScore = compositeFromScores(appScores);
  const overviewChips = [
    ...(data.classification?.amenities ?? []),
    ...(data.classification?.vibeTags ?? []),
    ...(data.classification?.crowdTags ?? []),
    ...(data.classification?.goodFor ?? []),
  ].slice(0, 18);
  const description = data.classification?.description ?? data.facts?.editorialSummary ?? data.livePlace?.nomadTip ?? "";

  async function saveIdentity() {
    const payload: AdminPlacePatchRequest = {
      place: {
        name: current.place.name,
        neighborhood: textOrNull(current.place.neighborhood),
        address: textOrNull(current.place.address),
        lat: numberOrNull(current.place.lat),
        lng: numberOrNull(current.place.lng),
        website: textOrNull(current.place.website),
        businessStatus: textOrNull(current.place.businessStatus),
        priceLevel: textOrNull(current.place.priceLevel),
        openHour: numberOrNull(current.place.openHour),
        closeHour: numberOrNull(current.place.closeHour),
      },
    };
    await apiSend(path, "PATCH", payload);
    toast.success("Identity saved");
    mutate(path);
  }

  async function saveClassification() {
    await apiSend(`${path}/classification`, "PATCH", {
      ...current.classification,
      format: textOrNull(current.classification.format),
      priceTier: textOrNull(current.classification.priceTier),
      meal: textOrNull(current.classification.meal),
      occasion: textOrNull(current.classification.occasion),
      groupType: textOrNull(current.classification.groupType),
      michelinStatus: textOrNull(current.classification.michelinStatus),
      noiseLevel: textOrNull(current.classification.noiseLevel),
      reservations: textOrNull(current.classification.reservations),
      touristRatio: textOrNull(current.classification.touristRatio),
      cashPolicy: textOrNull(current.classification.cashPolicy),
      description: textOrNull(current.classification.description),
      nomadTip: textOrNull(current.classification.nomadTip),
    });
    toast.success("Classification saved");
    mutate(path);
  }

  async function saveHygiene() {
    await apiSend(`${path}/hygiene`, "PATCH", { verdict: current.hygiene.verdict, reason: textOrNull(current.hygiene.reason), qualityFlags: current.hygiene.qualityFlags, reviewedBy: current.hygiene.reviewedBy || "admin" });
  }

  async function saveScores() {
    const payload: AdminPlaceScorePatchRequest = {
      environment: numberOrNull(current.score.environment),
      wifi: numberOrNull(current.score.wifi),
      outlets: numberOrNull(current.score.outlets),
      price: numberOrNull(current.score.price),
      noise: numberOrNull(current.score.noise),
    };
    await apiSend(`${path}/scores`, "PATCH", payload);
    toast.success("Scores saved");
    mutate(path);
  }

  async function saveMedia() {
    const photos: AdminPlacePhotosPatchRequest = { photos: current.photos.map((photo, index) => ({ ...photo, position: index })) };
    const clips: AdminPlaceClipsPatchRequest = { clips: current.clips.map((clip, index) => ({ ...clip, position: index, creator: textOrNull(clip.creator), gifUrl: textOrNull(clip.gifUrl), poster: textOrNull(clip.poster) })) };
    await apiSend(`${path}/photos`, "PATCH", photos);
    await apiSend(`${path}/clips`, "PATCH", clips);
    toast.success("Media saved");
    mutate(path);
  }

  async function saveHygieneAndToast() {
    await saveHygiene();
    toast.success("Quality check saved");
    mutate(path);
  }

  async function republish(force = false) {
    await apiSend(`${path}/republish`, "POST", { force });
    toast.success("Place republished");
    mutate(path);
  }

  async function rerunPipelineStageFor(stage: RerunStage) {
    const label = rerunStageOptions.find((option) => option.value === stage)?.label ?? stage;
    if (!window.confirm(`Rerun ${label.toLowerCase()} for ${data?.place.name ?? "this place"}?`)) return;
    const result = await apiSend<{ runId?: string } | null>(`${path}/rerun`, "POST", { stage, force: false });
    toast.success(result?.runId ? `${label} rerun started (run ${result.runId})` : `${label} rerun started`);
    mutate(path);
  }

  async function rerunPipelineStage() {
    await rerunPipelineStageFor(rerunStage);
  }

  const saveActions = {
    overview: (
      <>
        <Button onClick={() => republish(false).catch(showError)}><RefreshCw /> Republish</Button>
        <Button variant="outline" onClick={() => republish(true).catch(showError)}>Force</Button>
      </>
    ),
    media: <Button onClick={() => saveMedia().catch(showError)}><Save /> Save media</Button>,
    identity: <Button onClick={() => saveIdentity().catch(showError)}><Save /> Save identity</Button>,
    classification: <Button onClick={() => saveClassification().catch(showError)}><Save /> Save classification</Button>,
    hygiene: <Button onClick={() => saveHygieneAndToast().catch(showError)}><Save /> Save hygiene</Button>,
    scores: <Button onClick={() => saveScores().catch(showError)}><Save /> Save scores</Button>,
    pipeline: null,
  } satisfies Record<PlaceEditorTab, ReactNode>;

  const hero = <Hero data={data} media={media} status={status} actions={sheet ? null : <HeroActions onRepublish={() => republish(false).catch(showError)} onForce={() => republish(true).catch(showError)} />} />;
  const tabs = (
    <Tabs value={activeTab} onValueChange={(value) => { if (isPlaceEditorTab(value)) setActiveTab(value); }} className="flex min-h-full flex-col">
      <div className="sticky top-0 z-10 border-b bg-background/95 px-4 pt-3 backdrop-blur">
        <TabsList variant={sheet ? "line" : "default"} className="h-9">
          {placeEditorTabOrder.map((tab) => <TabsTrigger key={tab} value={tab}>{placeEditorTabs[tab]}</TabsTrigger>)}
        </TabsList>
      </div>

      <div className="px-5 py-5">
        <TabsContent value="overview" className="m-0">
          <div className="grid gap-7">
            <section className="grid gap-3">
              <SectionTitle title="Snapshot" eyebrow={`${data.place.category ?? data.classification?.format ?? "Place"} · ${data.place.neighborhood ?? data.place.citySlug}`} />
              <div className="grid grid-cols-3 gap-3">
                <Metric label="Rating" value={data.place.rating?.toFixed(1) ?? "—"} tone="amber" />
                <Metric label="Reviews" value={data.place.reviewCount?.toLocaleString() ?? "0"} tone="sky" />
                <Metric label="Composite" value={formatScore(compositeScore)} tone="emerald" />
              </div>
              {description ? <p className="text-sm leading-6 text-muted-foreground">{description}</p> : <p className="text-sm text-muted-foreground">No editorial description yet.</p>}
            </section>

            <section className="grid gap-3">
              <SectionTitle title="Signals" eyebrow="Amenities, vibe, crowd" />
              {overviewChips.length ? <ChipCloud values={overviewChips} /> : <p className="text-sm text-muted-foreground">No amenities or vibe tags recorded.</p>}
            </section>

            <section className="grid gap-3">
              <SectionTitle title="Scores" eyebrow="Curation profile" />
              <ScoreBars rows={scoreRows} compact />
            </section>

            <section className="grid gap-3">
              <SectionTitle title="Creator clips" eyebrow={`${media.clips.length} previews`} />
              <ClipGrid clips={media.clips} compact />
            </section>
          </div>
        </TabsContent>

        <TabsContent value="media" className="m-0">
          <div className="grid gap-8">
            <MediaStateBanner data={data} onRun={() => rerunPipelineStageFor("media").catch(showError)} />
            <section className="grid gap-3">
              <SectionTitle title="Video previews" eyebrow={`${media.clips.length} clips`} />
              <ClipGrid clips={media.clips} />
            </section>
            <section className="grid gap-3">
              <SectionTitle title="Photos" eyebrow={`${media.photos.length} images`} />
              <PhotoGrid photos={media.photos} name={data.place.name} />
            </section>
            <section className="grid gap-3 border-t pt-6">
              <SectionTitle title="Media records" eyebrow="Editable source refs" />
              <MediaEditor form={current} setForm={setForm} onSave={sheet ? undefined : () => saveMedia().catch(showError)} />
            </section>
          </div>
        </TabsContent>

        <TabsContent value="identity" className="m-0">
          <IdentitySection current={current} setForm={setForm} onSave={sheet ? undefined : () => saveIdentity().catch(showError)} />
        </TabsContent>

        <TabsContent value="classification" className="m-0">
          <ClassificationSection current={current} setForm={setForm} onSave={sheet ? undefined : () => saveClassification().catch(showError)} />
        </TabsContent>

        <TabsContent value="hygiene" className="m-0">
          <HygieneSection current={current} setForm={setForm} onSave={sheet ? undefined : () => saveHygieneAndToast().catch(showError)} />
        </TabsContent>

        <TabsContent value="scores" className="m-0">
          <ScoresSection current={current} setForm={setForm} rows={scoreRows} onSave={sheet ? undefined : () => saveScores().catch(showError)} />
        </TabsContent>

        <TabsContent value="pipeline" className="m-0">
          <div className="grid gap-7">
            <section className="grid gap-3">
              <SectionTitle title="Rerun a stage" eyebrow="Scoped to this place" />
              <p className="text-sm text-muted-foreground">
                Run any pipeline stage for just this place — e.g. <span className="font-medium">Photos & videos</span> to (re)discover creator clips. Best-effort; existing data is preserved.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <select aria-label="Rerun stage" className="h-9 w-44 rounded-lg border bg-background px-2 text-sm" value={rerunStage} onChange={(event) => setRerunStage(event.target.value as RerunStage)}>
                  {rerunStageOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <Button onClick={() => rerunPipelineStage().catch(showError)}><Play /> Rerun</Button>
              </div>
              <p className="text-xs text-muted-foreground">Queues an orchestrator run scoped to this place. Progress shows up under Runs.</p>
            </section>
          </div>
        </TabsContent>
      </div>
    </Tabs>
  );

  if (sheet) {
    return (
      <PlaceEditorSheetFrame header={hero} footer={saveActions[activeTab]}>
        {tabs}
      </PlaceEditorSheetFrame>
    );
  }

  return (
    <div className="mx-auto max-w-6xl bg-background">
      {hero}
      <div className="p-6">{tabs}</div>
    </div>
  );
}

function isPlaceEditorTab(value: string): value is PlaceEditorTab {
  return Object.prototype.hasOwnProperty.call(placeEditorTabs, value);
}

function PlaceEditorSheetFrame({ header, children, footer }: { header: ReactNode; children: ReactNode; footer: ReactNode }) {
  return (
    <div className="flex h-full min-h-0 flex-1 flex-col bg-background">
      <SheetHeader className="shrink-0 gap-0 p-0">{header}</SheetHeader>
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      <SheetFooter className="mt-0 min-h-14 shrink-0 flex-row flex-wrap items-center justify-end gap-2 border-t bg-background/95 p-3">
        {footer}
      </SheetFooter>
    </div>
  );
}

function PlaceEditorSheetLoadingHeader() {
  return (
    <div className="grid gap-3 border-b p-4">
      <Skeleton className="h-5 w-48" />
      <Skeleton className="h-4 w-64 max-w-full" />
    </div>
  );
}

function Hero({ data, media, status, actions }: { data: AdminPlaceGraph; media: PlaceMedia; status: PlaceStatus; actions?: ReactNode }) {
  return (
    <div className="relative h-64 shrink-0 overflow-hidden bg-[linear-gradient(135deg,oklch(0.2_0.02_260),oklch(0.42_0.11_38),oklch(0.24_0.05_150))]">
      {media.cover ? (
        <img src={media.cover} alt="" className="h-full w-full object-cover" onError={(event) => { event.currentTarget.style.display = "none"; }} />
      ) : (
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.22),transparent_28%),linear-gradient(135deg,rgba(16,185,129,0.35),rgba(245,158,11,0.3),rgba(15,23,42,0.8))]" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/88 via-black/35 to-black/10" />
      <div className="absolute inset-x-0 bottom-0 grid gap-4 p-5 text-white">
        <div className="flex items-center gap-2">
          <StatusPill status={status} />
          {data.place.rating != null && <span className="rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold backdrop-blur">★ {data.place.rating.toFixed(1)}</span>}
          {data.place.category && <span className="rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold backdrop-blur">{data.place.category}</span>}
        </div>
        <div className="flex items-end justify-between gap-4">
          <div className="min-w-0">
            <h2 className="truncate text-2xl font-semibold tracking-tight">{data.place.name}</h2>
            <p className="mt-1 text-sm text-white/75">{[data.place.neighborhood, data.place.address].filter(Boolean).join(" · ") || data.place.citySlug}</p>
          </div>
          {actions ? <div className="flex shrink-0 gap-2">{actions}</div> : null}
        </div>
      </div>
    </div>
  );
}

function HeroActions({ onRepublish, onForce }: { onRepublish: () => void; onForce: () => void }) {
  return (
    <>
      <Button variant="secondary" onClick={onRepublish}><RefreshCw /> Republish</Button>
      <Button variant="outline" className="border-white/25 bg-white/10 text-white hover:bg-white/20 hover:text-white" onClick={onForce}>Force</Button>
    </>
  );
}

function IdentitySection({ current, setForm, onSave }: { current: PlaceForm; setForm: (form: PlaceForm) => void; onSave?: () => void }) {
  return (
    <section className="grid gap-3">
      <SectionTitle title="Identity" eyebrow="Canonical place fields" />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Field label="Name"><Input value={current.place.name} onChange={(e) => setForm({ ...current, place: { ...current.place, name: e.target.value } })} /></Field>
        <Field label="Neighborhood"><Input value={current.place.neighborhood} onChange={(e) => setForm({ ...current, place: { ...current.place, neighborhood: e.target.value } })} /></Field>
        <Field label="Business status"><NativeSelect value={current.place.businessStatus} options={options.businessStatus} onChange={(businessStatus) => setForm({ ...current, place: { ...current.place, businessStatus } })} /></Field>
        <Field label="Address"><Input value={current.place.address} onChange={(e) => setForm({ ...current, place: { ...current.place, address: e.target.value } })} /></Field>
        <Field label="Website"><Input value={current.place.website} onChange={(e) => setForm({ ...current, place: { ...current.place, website: e.target.value } })} /></Field>
        <Field label="Price level"><Input value={current.place.priceLevel} onChange={(e) => setForm({ ...current, place: { ...current.place, priceLevel: e.target.value } })} /></Field>
        <Field label="Lat"><Input value={current.place.lat} onChange={(e) => setForm({ ...current, place: { ...current.place, lat: e.target.value } })} /></Field>
        <Field label="Lng"><Input value={current.place.lng} onChange={(e) => setForm({ ...current, place: { ...current.place, lng: e.target.value } })} /></Field>
        <Field label="Open hour"><Input value={current.place.openHour} onChange={(e) => setForm({ ...current, place: { ...current.place, openHour: e.target.value } })} /></Field>
        <Field label="Close hour"><Input value={current.place.closeHour} onChange={(e) => setForm({ ...current, place: { ...current.place, closeHour: e.target.value } })} /></Field>
      </div>
      {onSave ? <Button className="justify-self-start" onClick={onSave}><Save /> Save identity</Button> : null}
    </section>
  );
}

function ClassificationSection({ current, setForm, onSave }: { current: PlaceForm; setForm: (form: PlaceForm) => void; onSave?: () => void }) {
  return (
    <section className="grid gap-3">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Field label="Format"><NativeSelect value={current.classification.format} options={options.format} onChange={(format) => setForm({ ...current, classification: { ...current.classification, format } })} /></Field>
        <Field label="Price tier"><NativeSelect value={current.classification.priceTier} options={options.priceTier} onChange={(priceTier) => setForm({ ...current, classification: { ...current.classification, priceTier } })} /></Field>
        <Field label="Noise"><NativeSelect value={current.classification.noiseLevel} options={options.noiseLevel} onChange={(noiseLevel) => setForm({ ...current, classification: { ...current.classification, noiseLevel } })} /></Field>
        <Field label="Meal"><Input value={current.classification.meal} onChange={(e) => setForm({ ...current, classification: { ...current.classification, meal: e.target.value } })} /></Field>
        <Field label="Occasion"><Input value={current.classification.occasion} onChange={(e) => setForm({ ...current, classification: { ...current.classification, occasion: e.target.value } })} /></Field>
        <Field label="Group type"><Input value={current.classification.groupType} onChange={(e) => setForm({ ...current, classification: { ...current.classification, groupType: e.target.value } })} /></Field>
        <Field label="Michelin"><NativeSelect value={current.classification.michelinStatus} options={options.michelinStatus} onChange={(michelinStatus) => setForm({ ...current, classification: { ...current.classification, michelinStatus } })} /></Field>
        <Field label="Reservations"><NativeSelect value={current.classification.reservations} options={options.reservations} onChange={(reservations) => setForm({ ...current, classification: { ...current.classification, reservations } })} /></Field>
        <Field label="Tourist ratio"><NativeSelect value={current.classification.touristRatio} options={options.touristRatio} onChange={(touristRatio) => setForm({ ...current, classification: { ...current.classification, touristRatio } })} /></Field>
        <Field label="Cash policy"><NativeSelect value={current.classification.cashPolicy} options={options.cashPolicy} onChange={(cashPolicy) => setForm({ ...current, classification: { ...current.classification, cashPolicy } })} /></Field>
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <SwitchField label="Wifi" checked={current.classification.wifi} onChange={(wifi) => setForm({ ...current, classification: { ...current.classification, wifi } })} />
        <SwitchField label="Outlets" checked={current.classification.outlets} onChange={(outlets) => setForm({ ...current, classification: { ...current.classification, outlets } })} />
        <SwitchField label="Outdoor seating" checked={current.classification.outdoorSeating} onChange={(outdoorSeating) => setForm({ ...current, classification: { ...current.classification, outdoorSeating } })} />
      </div>
      <TagEditor label="Cuisine" values={current.classification.cuisine} suggestions={[]} onChange={(cuisine) => setForm({ ...current, classification: { ...current.classification, cuisine } })} />
      <TagEditor label="Amenities" values={current.classification.amenities} suggestions={[...options.amenities]} onChange={(amenities) => setForm({ ...current, classification: { ...current.classification, amenities } })} />
      <TagEditor label="Vibe tags" values={current.classification.vibeTags} suggestions={[...options.vibeTags]} onChange={(vibeTags) => setForm({ ...current, classification: { ...current.classification, vibeTags } })} />
      <TagEditor label="Mood" values={current.classification.mood} suggestions={[]} onChange={(mood) => setForm({ ...current, classification: { ...current.classification, mood } })} />
      <TagEditor label="Crowd tags" values={current.classification.crowdTags} suggestions={[...options.crowdTags]} onChange={(crowdTags) => setForm({ ...current, classification: { ...current.classification, crowdTags } })} />
      <TagEditor label="Good for" values={current.classification.goodFor} suggestions={[...options.goodFor]} onChange={(goodFor) => setForm({ ...current, classification: { ...current.classification, goodFor } })} />
      <Field label="Description"><Textarea value={current.classification.description} onChange={(e) => setForm({ ...current, classification: { ...current.classification, description: e.target.value } })} /></Field>
      <Field label="Nomad tip"><Textarea value={current.classification.nomadTip} onChange={(e) => setForm({ ...current, classification: { ...current.classification, nomadTip: e.target.value } })} /></Field>
      {onSave ? <Button className="justify-self-start" onClick={onSave}><Save /> Save classification</Button> : null}
    </section>
  );
}

function HygieneSection({ current, setForm, onSave }: { current: PlaceForm; setForm: (form: PlaceForm) => void; onSave?: () => void }) {
  const tone = current.hygiene.verdict === "cut" ? "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300" : current.hygiene.verdict === "flag" ? "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
  return (
    <section className="grid gap-5">
      <div className={`rounded-lg border px-4 py-3 ${tone}`}>
        <div className="text-sm font-semibold">Current verdict: {current.hygiene.verdict}</div>
        <div className="mt-1 text-xs opacity-80">{current.hygiene.reason || "No reason recorded."}</div>
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Field label="Verdict"><NativeSelect value={current.hygiene.verdict} options={options.hygieneVerdict} onChange={(verdict) => setForm({ ...current, hygiene: { ...current.hygiene, verdict: verdict as PlaceForm["hygiene"]["verdict"] } })} /></Field>
        <Field label="Reviewed by"><Input value={current.hygiene.reviewedBy} onChange={(e) => setForm({ ...current, hygiene: { ...current.hygiene, reviewedBy: e.target.value } })} /></Field>
      </div>
      <TagEditor label="Quality flags" values={current.hygiene.qualityFlags} suggestions={[...options.qualityFlags]} onChange={(qualityFlags) => setForm({ ...current, hygiene: { ...current.hygiene, qualityFlags } })} />
      <Field label="Reason"><Textarea value={current.hygiene.reason} onChange={(e) => setForm({ ...current, hygiene: { ...current.hygiene, reason: e.target.value } })} /></Field>
      {onSave ? <Button className="justify-self-start" onClick={onSave}><Save /> Save quality check</Button> : null}
    </section>
  );
}

function ScoresSection({ current, setForm, rows, onSave }: { current: PlaceForm; setForm: (form: PlaceForm) => void; rows: ScoreRow[]; onSave?: () => void }) {
  return (
    <section className="grid gap-6">
      <ScoreBars rows={rows} />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        {scoreKeys.map((key) => <Field key={key} label={scoreFields[key]}><Input value={current.score[key]} onChange={(e) => setForm({ ...current, score: { ...current.score, [key]: e.target.value } })} /></Field>)}
      </div>
      {onSave ? <Button className="justify-self-start" onClick={onSave}><Save /> Save scores</Button> : null}
    </section>
  );
}

function MediaEditor({ form, setForm, onSave }: { form: PlaceForm; setForm: (form: PlaceForm) => void; onSave?: () => void }) {
  return (
    <div className="grid gap-5">
      <div className="grid gap-2">
        <div className="text-sm font-medium">Photo refs</div>
        {form.photos.map((photo, index) => <div key={photo.id ?? index} className="grid grid-cols-[1fr_80px_32px] gap-2"><Input value={photo.ref} onChange={(e) => setForm({ ...form, photos: form.photos.map((item, i) => i === index ? { ...item, ref: e.target.value } : item) })} /><Input value={photo.position} onChange={(e) => setForm({ ...form, photos: form.photos.map((item, i) => i === index ? { ...item, position: Number(e.target.value) } : item) })} /><Button variant="ghost" size="icon-sm" onClick={() => setForm({ ...form, photos: form.photos.filter((_, i) => i !== index) })}><Trash2 /></Button></div>)}
        <Button className="justify-self-start" variant="outline" onClick={() => setForm({ ...form, photos: [...form.photos, { ref: "", position: form.photos.length }] })}>Add photo</Button>
      </div>
      <div className="grid gap-2 border-t pt-4">
        <div className="text-sm font-medium">Clip refs</div>
        {form.clips.map((clip, index) => <div key={clip.id ?? index} className="grid grid-cols-1 gap-2 rounded-md border p-2 xl:grid-cols-2"><Input placeholder="URL" value={clip.url} onChange={(e) => setClip(form, setForm, index, { url: e.target.value })} /><Input placeholder="Creator" value={clip.creator} onChange={(e) => setClip(form, setForm, index, { creator: e.target.value })} /><Input placeholder="GIF" value={clip.gifUrl} onChange={(e) => setClip(form, setForm, index, { gifUrl: e.target.value })} /><Input placeholder="Poster" value={clip.poster} onChange={(e) => setClip(form, setForm, index, { poster: e.target.value })} /></div>)}
        <Button className="justify-self-start" variant="outline" onClick={() => setForm({ ...form, clips: [...form.clips, { url: "", creator: "", gifUrl: "", poster: "", position: form.clips.length }] })}>Add clip</Button>
      </div>
      {onSave ? <Button className="justify-self-start" onClick={onSave}><Save /> Save media</Button> : null}
    </div>
  );
}

// Surfaces the three-state media coverage so a curator knows WHY a place has no
// clips: discovery genuinely found nothing ("not-found") vs. never ran
// ("pending"). Either way they can re-run. Hidden when clips exist.
function MediaStateBanner({ data, onRun }: { data: AdminPlaceGraph; onRun: () => void }) {
  const status = (data as { mediaStatus?: { state: "has" | "not-found" | "pending"; auditSummary: string | null } }).mediaStatus;
  if (!status || status.state === "has") return null;
  const notFound = status.state === "not-found";
  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 ${notFound ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-muted/40"}`}>
      <div className="min-w-0">
        <div className="text-sm font-semibold">{notFound ? "No creator clips found" : "Media hasn't been run yet"}</div>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {notFound
            ? (status.auditSummary || "Clip discovery ran and found nothing on TikTok or Google for this place — it likely has no creator coverage.")
            : "Clip discovery hasn't run for this place yet."}{" "}You can still run it.
        </p>
      </div>
      <Button variant="outline" onClick={onRun}><Play /> Fetch photos & videos</Button>
    </div>
  );
}

function ClipGrid({ clips, compact = false }: { clips: PlaceMedia["clips"]; compact?: boolean }) {
  if (!clips.length) return <EmptyState title="No clips" description="Add gifUrl-backed creator clips in the Media tab." />;
  return (
    <div className={`grid gap-3 ${compact ? "grid-cols-3" : "grid-cols-2 md:grid-cols-3"}`}>
      {clips.map((clip, index) => (
        <a key={`${clip.url}-${index}`} href={clip.url || clip.gifUrl || "#"} target="_blank" rel="noopener noreferrer" className="group relative overflow-hidden rounded-xl bg-black shadow-sm ring-1 ring-border" style={{ aspectRatio: "9 / 16" }}>
          {clip.poster && <img src={clip.poster} alt="" className="absolute inset-0 h-full w-full object-cover opacity-80" />}
          {clip.gifUrl ? <img src={clip.gifUrl} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" loading="lazy" onError={(event) => { event.currentTarget.style.display = "none"; }} /> : <div className="grid h-full place-items-center text-white/55"><Video /></div>}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-2">
            <div className="flex items-center gap-1 text-xs font-semibold text-white"><ExternalLink className="size-3" /> <span className="truncate">{clip.creator || "Open clip"}</span></div>
          </div>
        </a>
      ))}
    </div>
  );
}

function PhotoGrid({ photos, name }: { photos: string[]; name: string }) {
  if (!photos.length) return <EmptyState title="No photos" description="Add photo refs in the Media records section." />;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
      {photos.map((photo, index) => (
        <div key={`${photo}-${index}`} className="relative aspect-[4/3] overflow-hidden rounded-xl bg-muted ring-1 ring-border">
          <img src={photo} alt={`${name} photo ${index + 1}`} className="h-full w-full object-cover" loading="lazy" onError={(event) => { event.currentTarget.style.display = "none"; }} />
          <div className="absolute left-2 top-2 rounded-full bg-black/45 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur">{index + 1}</div>
        </div>
      ))}
    </div>
  );
}

function ScoreBars({ rows, compact = false }: { rows: ScoreRow[]; compact?: boolean }) {
  return (
    <div className={`grid gap-3 ${compact ? "" : "rounded-xl bg-muted/40 p-4"}`}>
      {rows.map(({ key, label, value }) => {
        const pct = scorePercent(value);
        return (
          <div key={key} className="grid gap-1.5">
            <div className="flex justify-between text-xs">
              <span className="font-medium">{label}</span>
              <span className="tabular-nums text-muted-foreground">{value == null ? "—" : value.toFixed(1)}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div className={`h-full rounded-full ${scoreTone(value)}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ChipCloud({ values }: { values: string[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {values.map((value, index) => <span key={`${value}-${index}`} className={`rounded-full border px-2.5 py-1 text-xs font-medium ${chipTone(index)}`}>{value}</span>)}
    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone: "amber" | "sky" | "emerald" }) {
  const classes = {
    amber: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
    sky: "bg-sky-500/10 text-sky-700 dark:text-sky-300",
    emerald: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  };
  return <div className={`rounded-xl px-3 py-2 ${classes[tone]}`}><div className="text-lg font-semibold tabular-nums">{value}</div><div className="text-[11px] font-medium opacity-75">{label}</div></div>;
}

function SectionTitle({ title, eyebrow }: { title: string; eyebrow: string }) {
  return <div className="border-b pb-2"><div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{eyebrow}</div><h3 className="mt-1 text-base font-semibold tracking-tight">{title}</h3></div>;
}

function StatusPill({ status }: { status: PlaceStatus }) {
  const classes = {
    live: "bg-emerald-500/90 text-white",
    cut: "bg-red-500/90 text-white",
    flag: "bg-amber-500/90 text-black",
    draft: "bg-white/18 text-white",
  };
  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold backdrop-blur ${classes[status.kind]}`}>{status.label}</span>;
}

function placeFormFromGraph(data: AdminPlaceGraph): PlaceForm {
  const scores = appScoresFromGraph(data);
  return {
    place: { name: data.place.name ?? "", neighborhood: data.place.neighborhood ?? "", address: data.place.address ?? "", lat: data.place.lat?.toString() ?? "", lng: data.place.lng?.toString() ?? "", website: data.place.website ?? "", businessStatus: data.place.businessStatus ?? "", priceLevel: data.place.priceLevel ?? "", openHour: data.place.openHour?.toString() ?? "", closeHour: data.place.closeHour?.toString() ?? "" },
    classification: { cuisine: data.classification?.cuisine ?? [], format: data.classification?.format ?? "", priceTier: data.classification?.priceTier ?? "", meal: data.classification?.meal ?? "", occasion: data.classification?.occasion ?? "", groupType: data.classification?.groupType ?? "", noiseLevel: data.classification?.noiseLevel ?? "", amenities: data.classification?.amenities ?? [], vibeTags: data.classification?.vibeTags ?? [], mood: data.classification?.mood ?? [], crowdTags: data.classification?.crowdTags ?? [], goodFor: data.classification?.goodFor ?? [], michelinStatus: data.classification?.michelinStatus ?? "", description: data.classification?.description ?? "", nomadTip: data.classification?.nomadTip ?? "", wifi: data.classification?.wifi ?? false, outlets: data.classification?.outlets ?? false, outdoorSeating: data.classification?.outdoorSeating ?? false, reservations: data.classification?.reservations ?? "", touristRatio: data.classification?.touristRatio ?? "", cashPolicy: data.classification?.cashPolicy ?? "" },
    hygiene: { verdict: data.hygiene?.verdict === "flag" || data.hygiene?.verdict === "cut" ? data.hygiene.verdict : "pass", reason: data.hygiene?.reason ?? "", qualityFlags: data.hygiene?.qualityFlags ?? [], reviewedBy: data.hygiene?.reviewedBy ?? "admin" },
    score: {
      environment: scores?.environment?.toString() ?? "",
      wifi: scores?.wifi?.toString() ?? "",
      outlets: scores?.outlets?.toString() ?? "",
      price: scores?.price?.toString() ?? "",
      noise: scores?.noise?.toString() ?? "",
    },
    photos: data.photos.map((photo) => ({ id: photo.id, ref: photo.ref, position: photo.position })),
    clips: data.clips.map((clip) => ({ id: clip.id, url: clip.url ?? "", creator: clip.creator ?? "", gifUrl: clip.gifUrl ?? "", poster: clip.poster ?? "", position: clip.position })),
  };
}

function mediaFromGraph(data: AdminPlaceGraph): PlaceMedia {
  const extra = data as AdminPlaceGraph & Record<string, unknown>;
  const mediaObject = objectValue(extra.media) ?? objectValue(extra.resolvedMedia) ?? objectValue(extra.mediaUrls);
  const livePlaceJson = objectValue(data.livePlace?.placeJson);
  const resolvedPhotos = [
    ...photoArray(mediaObject?.photos),
    ...photoArray(extra.photosResolved),
    ...photoArray(extra.photoUrls),
  ].map(resolvePhotoUrl);
  const normalizedPhotos = [
    data.livePlace?.coverAssetUrl,
    data.published?.coverAssetUrl,
    data.place.imageUrl,
    data.livePlace?.photo,
    data.facts?.heroPhotoRef,
    ...data.photos.map((photo) => photo.ref),
    ...(data.facts?.photoRefs ?? []),
  ].map(resolvePhotoUrl);
  const photos = dedupe([...resolvedPhotos, ...normalizedPhotos]);
  const cover = firstString([
    stringValue(extra.coverUrl),
    stringValue(extra.cover),
    stringValue(extra.resolvedCoverUrl),
    stringValue(mediaObject?.coverUrl),
    stringValue(mediaObject?.cover),
    stringValue(mediaObject?.heroUrl),
    data.livePlace?.coverAssetUrl,
    data.published?.coverAssetUrl,
    photos[0],
  ].map(resolvePhotoUrl));
  const clips = dedupeClips([
    ...clipArray(mediaObject?.clips),
    ...clipArray(extra.resolvedClips),
    ...clipArray(data.livePlace?.clipsJson),
    ...clipArray(livePlaceJson?.clips),
    ...data.clips.map((clip) => ({ gifUrl: resolveAssetUrl(clip.gifUrl), poster: resolveAssetUrl(clip.poster), url: clip.url ?? "", creator: clip.creator, position: clip.position })),
  ]);
  return { cover, photos, clips };
}

function resolveAssetUrl(value?: string | null) {
  if (!value) return "";
  if (value.startsWith("http") || value.startsWith("/api/")) return value;
  if (value.startsWith("/")) return value;
  return value;
}

function resolvePhotoUrl(value?: string | null) {
  if (!value) return "";
  const asset = resolveAssetUrl(value);
  if (asset !== value || value.startsWith("http") || value.startsWith("/") || value.startsWith("/api/")) return asset;
  return `/api/cafe-photo?ref=${encodeURIComponent(value)}`;
}

function objectValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function firstString(values: string[]) {
  return values.find(Boolean) ?? "";
}

function photoArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    if (typeof item === "string") return item;
    const object = objectValue(item);
    return stringValue(object?.url) || stringValue(object?.ref) || stringValue(object?.src) || stringValue(object?.coverAssetUrl);
  }).filter(Boolean);
}

function clipArray(value: unknown): PlaceMedia["clips"] {
  if (!Array.isArray(value)) return [];
  return value.map((item, index) => {
    const object = objectValue(item);
    if (!object) return null;
    const gifUrl = resolveAssetUrl(stringValue(object.gifUrl) || stringValue(object.gif) || stringValue(object.previewUrl));
    const poster = resolveAssetUrl(stringValue(object.poster) || stringValue(object.posterUrl));
    const url = stringValue(object.url) || stringValue(object.sourceUrl) || gifUrl;
    if (!gifUrl && !url && !poster) return null;
    return {
      url,
      creator: stringValue(object.creator) || stringValue(object.author) || null,
      gifUrl,
      poster,
      position: typeof object.position === "number" ? object.position : index,
    };
  }).filter((item): item is PlaceMedia["clips"][number] => Boolean(item));
}

function dedupeClips(clips: PlaceMedia["clips"]) {
  const seen = new Set<string>();
  return clips
    .filter((clip) => {
      const key = clip.gifUrl || clip.url || clip.poster;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.position - b.position);
}

function statusForPlace(data: AdminPlaceGraph): PlaceStatus {
  if (data.place.cutReason || data.hygiene?.verdict === "cut") return { kind: "cut", label: "Cut" };
  if (data.hygiene?.verdict === "flag" || (data.hygiene?.qualityFlags?.length ?? 0) > 0) return { kind: "flag", label: "Flagged" };
  if (data.published) return { kind: "live", label: "Live" };
  return { kind: "draft", label: "Draft" };
}

function appScoresFromGraph(data: AdminPlaceGraph): AppScores | null {
  return appScoresFromValue(data.livePlace?.scoresJson)
    ?? appScoresFromValue(objectValue(data.livePlace?.placeJson)?.scores)
    ?? legacyScoreToAppScores(data.score);
}

function appScoresFromValue(value: unknown): AppScores | null {
  const object = objectValue(value);
  if (!object) return null;
  const scores = Object.fromEntries(scoreKeys.map((key) => [key, numberValue(object[key])])) as Partial<AppScores>;
  return scoreKeys.some((key) => scores[key] != null) ? scores as AppScores : null;
}

function legacyScoreToAppScores(score: AdminPlaceGraph["score"]): AppScores | null {
  if (!score) return null;
  const scores = {
    environment: score.food ?? null,
    wifi: score.amenities ?? null,
    outlets: score.access ?? null,
    price: score.value ?? null,
    noise: score.vibe ?? null,
  };
  return scoreKeys.some((key) => scores[key] != null) ? scores : null;
}

function numberValue(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function compositeFromScores(scores: AppScores | null) {
  if (!scores) return null;
  let total = 0;
  for (const key of scoreKeys) {
    const value = scores[key];
    if (value == null) return null;
    total += value;
  }
  return Math.round((total / scoreKeys.length) * 10) / 10;
}

function scorePercent(value: number | null | undefined) {
  if (value == null) return 0;
  return Math.min(100, Math.max(0, value * 20));
}

function scoreTone(value: number | null | undefined) {
  if (value == null) return "bg-muted-foreground/30";
  if (value >= 4) return "bg-emerald-500";
  if (value >= 3) return "bg-amber-500";
  return "bg-red-500";
}

function formatScore(value: number | null | undefined) {
  return value == null ? "—" : value.toFixed(1);
}

function chipTone(index: number) {
  return [
    "border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    "border-sky-500/20 bg-sky-500/10 text-sky-700 dark:text-sky-300",
    "border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300",
    "border-fuchsia-500/20 bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300",
  ][index % 4];
}

function dedupe(values: string[]) {
  const seen = new Set<string>();
  return values.filter((value) => {
    if (!value || seen.has(value)) return false;
    seen.add(value);
    return true;
  });
}

function setClip(form: PlaceForm, setForm: (form: PlaceForm) => void, index: number, patch: Partial<PlaceForm["clips"][number]>) {
  setForm({ ...form, clips: form.clips.map((clip, i) => i === index ? { ...clip, ...patch } : clip) });
}

function showError(error: unknown) {
  toast.error(error instanceof Error ? error.message : "Request failed");
}

type PlaceStatus = { kind: "live" | "cut" | "flag" | "draft"; label: string };
type AppScoreKey = typeof scoreKeys[number];
type AppScores = Partial<Record<AppScoreKey, number | null>>;
type ScoreRow = { key: AppScoreKey; label: string; value: number | null };
type PlaceMedia = {
  cover: string;
  photos: string[];
  clips: Array<{ url: string; creator: string | null; gifUrl: string; poster: string; position: number }>;
};
